import React, { useState, useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Tile } from '@/components/bento';

// ─── Start from an idea: category tabs + prompt cards ────────────────────────
// Each card is a screenshot of a purpose-built demo page, photographed inside a
// macOS Safari window by tools/showcase-thumbs/capture.cjs (the `idea-*`
// targets; sources in tools/showcase-thumbs/pages/ideas). The pages use real
// stock photography, so a card looks like a site someone actually shipped.
//
// Every card is a prompt-starter: the prompt printed on it is exactly what
// clicking it drops into the hero's box via onUseIdea(prompt). The screenshots
// are illustrations of the idea, not BuildersLab output — the copy around them
// must not claim otherwise.

const CATEGORIES = ['Websites', 'Games', 'Tools'];

const IMG = '/images/showcase/ideas';

const SHOWCASE = {
  Websites: [
    {
      name: 'Ceramics store',
      img: `${IMG}/ceramics.jpg`,
      alt: 'Terra & Kiln — a handmade ceramics store with an editorial hero and a product grid',
      prompt: 'An online store for handmade ceramics with an editorial hero, a product grid with prices and a cart. Warm cream palette and serif headings.',
    },
    {
      name: 'Wood-fire restaurant',
      img: `${IMG}/restaurant.jpg`,
      alt: 'FUMO — a wood-fire restaurant with a full-screen fire photo and tonight’s menu',
      prompt: 'A restaurant website for a wood-fire kitchen: a full-screen photo hero, tonight’s menu with prices, opening hours and a book-a-table button.',
    },
    {
      name: 'Real estate listings',
      img: `${IMG}/realestate.jpg`,
      alt: 'Harbor & Pine — homes for sale with listing cards and a map of price pins',
      prompt: 'A real estate site with a search bar, listing cards with photos, prices, beds and baths, and a map with price pins.',
    },
    {
      name: 'Boutique hotel',
      img: `${IMG}/hotel.jpg`,
      alt: 'Casa Azul — a boutique hotel in Lisbon with a full-screen street photo and a booking bar',
      prompt: 'A boutique hotel website with a full-screen photo, a booking bar for dates and guests, and rooms with nightly rates.',
    },
    {
      name: 'Wedding photographer',
      img: `${IMG}/photographer.jpg`,
      alt: 'Ada Okoro — a wedding photographer portfolio with a masonry photo grid',
      prompt: 'A portfolio for a wedding photographer with a masonry photo gallery, category filters and a check-your-date form.',
    },
  ],
  Games: [
    {
      name: 'Chess',
      img: `${IMG}/chess.jpg`,
      alt: 'Checkmate Club — a chess game with a wooden board, a move list and two clocks',
      prompt: 'A playable chess game with a wooden board, legal-move highlights, a move list, captured pieces and two player clocks.',
    },
    {
      name: 'Memory match',
      img: `${IMG}/memory.jpg`,
      alt: 'Safari Match — a memory card game with wildlife photos, a moves counter and a timer',
      prompt: 'A memory card game with wildlife photos, a moves counter, a timer, three difficulty levels and a best-times board.',
    },
    {
      name: 'Sliding photo puzzle',
      img: `${IMG}/slider.jpg`,
      alt: 'Slide & Solve — a 4×4 sliding puzzle made from a mountain lake photo',
      prompt: 'A sliding photo puzzle with a 4×4 grid, a move counter, a shuffle button and the finished picture as a hint. Players can upload their own photo.',
    },
    {
      name: 'Picture trivia',
      img: `${IMG}/trivia.jpg`,
      alt: 'Where in the World? — a picture trivia game showing the Colosseum and four answers',
      prompt: 'A picture trivia game that shows a photo of a famous place, four answer buttons, a countdown bar, a score and a fun fact after each answer.',
    },
    {
      name: 'Solitaire',
      img: `${IMG}/solitaire.jpg`,
      alt: 'Felt Solitaire — a Klondike solitaire game on green felt',
      prompt: 'A classic Klondike solitaire game on green felt with drag-and-drop cards, undo, hints, a timer and a score.',
    },
  ],
  Tools: [
    {
      name: 'Weather app',
      img: `${IMG}/weather.jpg`,
      alt: 'Skyline — a weather app over a photo of the Lagos skyline with hourly and 7-day forecasts',
      prompt: 'A weather app showing the current conditions over a photo of the city, an hourly strip, a 7-day forecast and air quality.',
    },
    {
      name: 'Recipe scaler',
      img: `${IMG}/recipe.jpg`,
      alt: 'mise — a jollof rice recipe with a servings stepper and scaled ingredients',
      prompt: 'A recipe page that rescales every ingredient when you change the servings, with the dish photo, metric and US units, and a shopping-list button.',
    },
    {
      name: 'Mortgage calculator',
      img: `${IMG}/mortgage.jpg`,
      alt: 'HomeMath — a mortgage calculator with sliders, a payment breakdown and a balance chart',
      prompt: 'A mortgage calculator with sliders for price, deposit, rate and term, a monthly payment breakdown chart and the loan balance over time.',
    },
    {
      name: 'Travel budget planner',
      img: `${IMG}/travel.jpg`,
      alt: 'wayfare — a travel budget planner with trip cards and spending charts',
      prompt: 'A travel budget planner with trip cards, a spending breakdown by category, a daily spending chart and a list of expenses.',
    },
    {
      name: 'Colour palette extractor',
      img: `${IMG}/palette.jpg`,
      alt: 'Palette Pull — a tool that extracts a five-colour palette from a photo of a spice market',
      prompt: 'A tool that pulls the main colours out of any photo, with hex codes to copy, a contrast check and CSS variables to export.',
    },
  ],
};

// ── Components ────────────────────────────────────────────────────────────────

/**
 * The whole "Start from an idea" tile — heading, one line of copy and the
 * gallery — as the homepage and the dashboard both show it, so the two cannot
 * drift apart. Each page decides what a picked prompt does via onUseIdea: both
 * put it in their own prompt box, which in both places sits above this tile.
 */
export function StartFromAnIdea({ onUseIdea, className = '' }) {
  return (
    <Tile className={`p-5 sm:p-8 min-w-0 ${className}`}>
      <h2 className="text-2xl font-extrabold tracking-tight text-white">Start from an idea</h2>
      <p className="text-zinc-400 text-[15px] mt-1">
        Pick a card and its prompt drops into the box above. Change anything you like, then hit Build.
      </p>
      <ShowcaseGallery onUseIdea={onUseIdea} />
    </Tile>
  );
}

export default function ShowcaseGallery({ onUseIdea }) {
  const [active, setActive] = useState('Websites');
  const [paused, setPaused] = useState(false);
  // Once someone picks a category it stays picked: rotating away from the
  // prompts they chose to read is the carousel working against them.
  const [chosen, setChosen] = useState(false);

  // Auto-advance categories; pause while the user is exploring.
  useEffect(() => {
    if (paused || chosen) return undefined;
    const timer = setInterval(() => {
      setActive(cur => CATEGORIES[(CATEGORIES.indexOf(cur) + 1) % CATEGORIES.length]);
    }, 7000);
    return () => clearInterval(timer);
  }, [paused, chosen]);

  return (
    <div
      className="mt-6"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Category switcher — pills, the Bento control used across the site. */}
      <div role="group" aria-label="Idea category" className="inline-flex max-w-full overflow-x-auto items-center gap-1 rounded-full border border-zinc-800 bg-zinc-950 p-1 mb-5 select-none">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => { setActive(cat); setChosen(true); }}
            aria-pressed={active === cat}
            className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold transition-colors duration-200 ${
              active === cat ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-white'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* A swipeable row on a phone, a grid from `sm` up — five across on a
          wide screen, where all of a category fits at once. */}
      <div
        key={active}
        className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2 custom-scrollbar sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 md:grid-cols-3 xl:grid-cols-5"
      >
        {SHOWCASE[active].map(({ name, img, alt, prompt }) => (
          <button
            key={name}
            type="button"
            onClick={() => onUseIdea?.(prompt)}
            title={`Use this prompt: ${name}`}
            className="group flex w-[264px] shrink-0 snap-start flex-col rounded-2xl border border-zinc-800 bg-zinc-950 p-2 text-left transition-colors hover:border-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 sm:w-auto"
          >
            {/* Screenshot, framed as a Mac browser window */}
            <span className="block overflow-hidden rounded-xl bg-zinc-900">
              <img
                src={img}
                alt={alt}
                loading="lazy"
                decoding="async"
                width={768}
                height={511}
                className="block aspect-[768/511] w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </span>

            <span className="flex flex-1 flex-col px-2 pb-2 pt-3">
              <span className="text-sm font-bold text-white">{name}</span>
              <span className="mt-1.5 text-[13px] leading-relaxed text-zinc-400">{prompt}</span>
              <span className="mt-auto flex items-center gap-1 pt-3 text-[13px] font-semibold text-orange-400">
                Use this prompt <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
